/* Shared, muted inline playback for real product and customer clips. */
(function () {
  'use strict';
  var states = [];
  var retryDelays = [350, 1000, 2200];
  var viewportObserver;
  var loadObserver;
  var pageReady = false;
  var motionQuery = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function reducedMotion() {
    return !!(motionQuery && motionQuery.matches);
  }

  function saveData() {
    var c = navigator.connection;
    return !!(c && (c.saveData || /^(slow-2g|2g|3g)$/.test(c.effectiveType || '')));
  }

  function tapOnly(video) {
    if (reducedMotion() || saveData() || video.hasAttribute('data-ugc-tap')) return true;
    return video.hasAttribute('data-ugc-mobile-tap') && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 749px)').matches;
  }

  function hasSource(video) {
    return !!(video.getAttribute('src') || video.getAttribute('data-src') || video.querySelector('source[src], source[data-src]'));
  }

  function active(state) {
    return (pageReady || state.manual) && state.visible && !document.hidden && !state.userPaused && !state.photoShown && !state.video.hidden && state.video.isConnected;
  }

  function status(state, value) {
    state.root.setAttribute('data-ugc-state', value);
    if (state.notice) {
      state.notice.hidden = value !== 'error';
      if (value === 'error') {
        var source = state.video.querySelector('source[src], source[data-src]');
        state.noticeLink.href = state.video.getAttribute('src') || (source && (source.getAttribute('src') || source.getAttribute('data-src'))) || '';
      }
    }
    if (!state.toggle) return;
    state.toggle.hidden = state.photoShown || !hasSource(state.video);
    var playing = value === 'playing';
    state.toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    state.toggle.classList.toggle('is-paused', !playing);
  }

  function cancelRetry(state) {
    if (state.timer) window.clearTimeout(state.timer);
    state.timer = 0;
  }

  function pause(state) {
    cancelRetry(state);
    // Invalidate pending promises before a source change or a viewport pause.
    state.generation += 1;
    state.pending = false;
    if (!state.video.paused) {
      state.controllerPauses += 1;
      state.video.pause();
    }
  }

  function load(state) {
    var video = state.video;
    var changed = false;
    if (video.hasAttribute('data-src')) {
      video.src = video.getAttribute('data-src');
      video.removeAttribute('data-src');
      changed = true;
    }
    video.querySelectorAll('source[data-src]').forEach(function (source) {
      // Select a declared smaller hero clip once, before its first request.
      // Keep that stream on resize so playback and the buyer's pause survive.
      var mobileSrc = source.getAttribute('data-mobile-src');
      var useMobile = mobileSrc && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 599px)').matches;
      source.src = useMobile ? mobileSrc : source.getAttribute('data-src');
      source.removeAttribute('data-src');
      source.removeAttribute('data-mobile-src');
      changed = true;
    });
    if (changed) {
      video.preload = 'auto';
      video.load();
    }
    return hasSource(video);
  }

  // Tap-only clips on the browser's own controls get real sources now; preload stays none,
  // so the play button works and nothing downloads until the visitor presses it.
  function prime(state) {
    var video = state.video;
    video.querySelectorAll('source[data-src]').forEach(function (old) {
      var mobileSrc = old.getAttribute('data-mobile-src');
      var useMobile = mobileSrc && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 599px)').matches;
      var source = document.createElement('source');
      source.type = old.type;
      source.src = useMobile ? mobileSrc : old.getAttribute('data-src');
      old.replaceWith(source);
    });
  }

  function failed(state, error, generation) {
    if (generation !== state.generation) return;
    state.pending = false;
    if (!active(state)) return;
    if (tryWebm(state)) return;
    status(state, state.video.error ? 'error' : 'blocked');
    // A bounded retry handles ready-state races. Never poll play() indefinitely.
    if (!state.video.error && state.attempts <= retryDelays.length && !state.timer) {
      state.timer = window.setTimeout(function () {
        state.timer = 0;
        play(state, false);
      }, retryDelays[state.attempts - 1]);
    }
  }

  function tryWebm(state) {
    var video = state.video;
    var alternate = video.querySelector('source[type="video/webm"]');
    var src = alternate && (alternate.getAttribute('src') || alternate.getAttribute('data-src'));
    // Some embedded Chromium builds advertise MP4 but lack an H264 decoder.
    // Recover once using the explicitly declared alternate, never a guessed URL.
    if (!src || state.webmTried || !video.error || (video.error.code !== 3 && video.error.code !== 4)) return false;
    pause(state);
    state.webmTried = true;
    video.src = src;
    video.load();
    state.attempts = 0;
    state.root.classList.remove('is-playing');
    status(state, 'ready');
    sync(state, true);
    return true;
  }

  function play(state, fromGesture) {
    var video = state.video;
    if ((!fromGesture && !active(state)) || state.pending || (!fromGesture && state.attempts > retryDelays.length)) return;
    if (fromGesture) recoverNetwork(state);
    if (!load(state)) return;
    if (!video.paused && !video.ended) return;
    // Muted/defaultMuted must be properties as well as HTML attributes in iOS webviews.
    // Once the buyer deliberately turns on sound, keep their audio choice.
    if (!state.userAudio) {
      video.defaultMuted = true;
      video.muted = true;
    }
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    cancelRetry(state);
    state.pending = true;
    state.attempts += 1;
    var generation = state.generation;
    var attempt;
    try {
      attempt = video.play();
    } catch (error) {
      failed(state, error, generation);
      return;
    }
    if (attempt && typeof attempt.then === 'function') {
      attempt.then(function () {
        if (generation !== state.generation) return;
        state.pending = false;
        if (!active(state)) pause(state);
      }).catch(function (error) { failed(state, error, generation); });
    } else {
      state.pending = false;
    }
  }

  function sync(state, reset) {
    if (reset) state.attempts = 0;
    if (active(state)) play(state, false);
    else pause(state);
  }

  function onScreen(video) {
    var rect = video.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
  }

  function recoverNetwork(state) {
    if (!state.video.error || (state.video.error.code !== 1 && state.video.error.code !== 2)) return;
    pause(state);
    state.video.load();
    state.attempts = 0;
    status(state, 'ready');
  }

  function initialize(video) {
    if (video.__ugcPlayback) return;
    var root = video.closest('[data-reel]') || video.parentElement;
    var state = {
      video: video, root: root, toggle: root.querySelector('[data-reel-toggle]'),
      visible: false, userPaused: tapOnly(video), manual: tapOnly(video), photoShown: video.hidden, userAudio: false,
      pending: false, attempts: 0, generation: 0, timer: 0, interactionUntil: 0, controllerPauses: 0, webmTried: false
    };
    video.__ugcPlayback = state;
    states.push(state);
    video.defaultMuted = true;
    video.muted = true;
    video.autoplay = !state.manual;
    video.loop = !reducedMotion();
    video.playsInline = true;
    if (state.toggle) video.removeAttribute('controls');
    video.setAttribute('muted', '');
    if (state.manual) video.removeAttribute('autoplay');
    else video.setAttribute('autoplay', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    state.notice = document.createElement('p');
    state.notice.className = 'ugc-media-notice';
    state.notice.hidden = true;
    state.notice.setAttribute('role', 'status');
    state.notice.appendChild(document.createTextNode('This video could not load. '));
    state.noticeLink = document.createElement('a');
    state.noticeLink.textContent = 'Open the video';
    state.notice.appendChild(state.noticeLink);
    root.appendChild(state.notice);
    status(state, state.manual ? 'paused' : 'ready');
    if (state.manual && !state.toggle) prime(state);

    video.addEventListener('playing', function () {
      state.pending = false;
      cancelRetry(state);
      if (!active(state)) { pause(state); return; }
      root.classList.add('is-playing');
      status(state, 'playing');
    });
    video.addEventListener('pause', function () {
      // Native controls can pause too: a deliberate pause persists across scroll/resume.
      if (state.controllerPauses > 0) state.controllerPauses -= 1;
      else if (state.visible && !document.hidden && Date.now() < state.interactionUntil) state.userPaused = true;
      status(state, state.photoShown ? 'photo' : 'paused');
    });
    video.addEventListener('play', function () {
      if (Date.now() < state.interactionUntil) state.userPaused = false;
    });
    video.addEventListener('volumechange', function () { state.userAudio = !video.muted; });
    video.addEventListener('click', function () {
      if (!state.manual || !video.paused || video.readyState > 0) return;
      state.userPaused = false;
      state.attempts = 0;
      play(state, true);
    });
    ['pointerdown', 'touchstart', 'keydown', 'click'].forEach(function (eventName) {
      video.addEventListener(eventName, function () { state.interactionUntil = Date.now() + 1500; }, { passive: true });
    });
    ['loadedmetadata', 'loadeddata', 'canplay'].forEach(function (eventName) {
      video.addEventListener(eventName, function () { sync(state, false); });
    });
    video.addEventListener('error', function () {
      if (tryWebm(state)) return;
      cancelRetry(state);
      state.pending = false;
      root.classList.remove('is-playing');
      status(state, 'error');
    });

    if (state.toggle) state.toggle.addEventListener('click', function () {
      if (video.paused) {
        state.userPaused = false;
        state.attempts = 0;
        play(state, true);
      } else {
        state.userPaused = true;
        pause(state);
        status(state, 'paused');
      }
    });

    // Preserve the product gallery's existing photo/clip thumbnail contract.
    root.addEventListener('reel:play', function (event) {
      var src = event.detail && event.detail.src;
      var webm = event.detail && event.detail.webm;
      pause(state);
      state.webmTried = false;
      if (src) {
        var source = video.querySelector('source');
        if (!source) {
          source = document.createElement('source');
          source.type = 'video/mp4';
          video.appendChild(source);
        }
        var changed = video.hasAttribute('src') || source.getAttribute('src') !== src;
        video.removeAttribute('src');
        source.removeAttribute('data-src');
        source.removeAttribute('data-mobile-src');
        source.src = src;
        var alternate = video.querySelector('source[type="video/webm"]');
        // Gallery fallbacks are supplied explicitly by the bundled thumbnail markup.
        // Remove the old clip's fallback when an arbitrary hosted MP4 is selected.
        if (webm) {
          if (!alternate) {
            alternate = document.createElement('source');
            alternate.type = 'video/webm';
            video.appendChild(alternate);
            changed = true;
          }
          alternate.removeAttribute('data-src');
          if (alternate.getAttribute('src') !== webm) changed = true;
          alternate.src = webm;
        } else if (alternate) {
          alternate.remove();
          changed = true;
        }
        if (changed) video.load();
      }
      video.hidden = false;
      state.photoShown = false;
      state.userPaused = false;
      state.attempts = 0;
      state.visible = onScreen(video);
      status(state, 'ready');
      play(state, true);
    });
    root.addEventListener('reel:stop', function () {
      state.photoShown = true;
      state.userPaused = true;
      pause(state);
      video.hidden = true;
      root.classList.remove('is-playing');
      status(state, 'photo');
    });

    if (viewportObserver) {
      viewportObserver.observe(video);
      loadObserver.observe(video);
    } else {
      state.visible = onScreen(video);
      sync(state, true);
    }
  }

  function scan(scope) {
    scope.querySelectorAll('video[data-ugc-video]').forEach(initialize);
  }

  function resume() {
    states = states.filter(function (state) {
      if (state.video.isConnected) return true;
      pause(state);
      if (viewportObserver) { viewportObserver.unobserve(state.video); loadObserver.unobserve(state.video); }
      return false;
    });
    states.forEach(function (state) {
      if (!viewportObserver) state.visible = onScreen(state.video);
      if (active(state)) recoverNetwork(state);
      sync(state, true);
    });
  }

  function start() {
    if ('IntersectionObserver' in window) {
      viewportObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var state = entry.target.__ugcPlayback;
          if (!state) return;
          // Observe the media itself. A tall phone clip never needs 60% of its whole card visible.
          var visible = entry.isIntersecting && entry.intersectionRatio >= 0.08;
          var reentered = visible && !state.visible;
          state.visible = visible;
          sync(state, reentered);
        });
      }, { threshold: [0, 0.08, 0.25, 0.6, 1] });
      loadObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || !pageReady) return;
          var state = entry.target.__ugcPlayback;
          if (state && state.manual) return;
          if (state) load(state);
          loadObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px', threshold: 0 });
    }
    scan(document);
    function markReady() {
      if (pageReady) return;
      pageReady = true;
      states.forEach(function (state) {
        if (loadObserver && !state.manual) { loadObserver.unobserve(state.video); loadObserver.observe(state.video); }
      });
      resume();
    }
    function whenIdle() {
      if ('requestIdleCallback' in window) window.requestIdleCallback(markReady, { timeout: 1500 });
      else window.setTimeout(markReady, 200);
    }
    if (document.readyState === 'complete') whenIdle();
    else window.addEventListener('load', whenIdle, { once: true });
    if (motionQuery && typeof motionQuery.addEventListener === 'function') {
      motionQuery.addEventListener('change', function () {
        states.forEach(function (state) {
          var manual = tapOnly(state.video);
          if (manual && !state.manual) { state.manual = true; state.userPaused = true; state.video.loop = false; pause(state); status(state, 'paused'); }
          else if (!manual && state.manual) { state.manual = false; state.video.loop = true; }
        });
      });
    }
    // Page restored from back/forward cache, app-switch, and webview focus recovery.
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('pageshow', resume);
    window.addEventListener('focus', resume);
    window.addEventListener('online', resume);
    document.addEventListener('shopify:section:load', function (event) { scan(event.target); });
    document.addEventListener('shopify:section:unload', function (event) {
      states = states.filter(function (state) {
        if (!event.target.contains(state.video)) return true;
        pause(state);
        if (viewportObserver) { viewportObserver.unobserve(state.video); loadObserver.unobserve(state.video); }
        return false;
      });
    });
    if (!viewportObserver) {
      var queued = false;
      function fallbackVisibility() {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () { queued = false; resume(); });
      }
      window.addEventListener('scroll', fallbackVisibility, { passive: true, capture: true });
      window.addEventListener('resize', fallbackVisibility, { passive: true });
    }
    // A normal page gesture can unlock a stricter in-app browser without a Play tap.
    function unlock(event) {
      // A gesture on a playback control must keep its intended play/pause behavior.
      if (event.target && event.target.closest && event.target.closest('video, [data-reel-toggle]')) return;
      states.forEach(function (state) {
        if (active(state) && state.video.paused) { state.attempts = 0; play(state, true); }
      });
    }
    document.addEventListener('pointerup', unlock, { passive: true });
    document.addEventListener('touchend', unlock, { passive: true });
    document.addEventListener('keydown', unlock);
  }

  // Start at DOM ready; waiting for every image/window load delays social visitors.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
